import { Observable } from 'rxjs/Observable';
import { AppConfig } from '../../../app/app.config';
import { PaginationModel } from '../../../core/models/pagination.model';
import { ActionOptionsArgs } from '../../../core/store/actions/base-actions';
import { isInArray } from '../../../core/util/is-in-array';
import { intersect } from '../../../core/util/utils';
import { VasState } from '../../store/store-type';
import { Field } from '../field/field.model';
import { File } from '../file/file.model';
import { JobStatus } from '../job-status/job-status.model';
import { JobType } from '../job-type/job-type.model';
import { Qry } from '../query-facade';
import { Report } from '../report/report.model';
import { RestrictedAccountObjectStoreFacade } from '../restricted-account-object/restricted-account-object.store-facade';
import * as actions from './job.actions';
import { Job } from './job.model';
import * as jobState from './job.reducer';

export class JobStoreFacade extends RestrictedAccountObjectStoreFacade<Job> {

    get(id: string): Observable<Job> {
        return this.select(Qry.getInstance().job.byId(id));
    }

    getAll(): Observable<Array<Job>> {
        return this.select(Qry.getInstance().job.byActiveAccount);
    }

    getAllInlined(): Observable<Array<Job>> { // @todo change the return type to JobInlined
        return this.select(Qry.getInstance().job.byActiveAccountInlined);
    }

    getInlined(id: string): Observable<Job> { // @todo change the return type to JobInlined
        return this.getAllInlined()
            .map(jobs => jobs.find(job => job.id === id));
    }

    getPaginationModel(): Observable<PaginationModel> {
        return this.select<jobState.State>((state: VasState) => state.job)
            .map((state: jobState.State) => new PaginationModel({
                total: state.total,
                limit: state.filters.limit,
                offset: state.filters.offset,
                localObjectCount: state.ids.length
            }));
    }

    getUiLoading(): Observable<boolean> {
        return this.select<jobState.State>((state: VasState) => state.job)
            .map(state => state.ui.loading);
    }

    create(job: Job): void {
        job = new Job({
            ...job,
            pendingFields: job.fields.length
        });
        this.submit(job);
    }

    update(job: Job): void {
        this.submit(job);
    }

    insert(job: Job): void {
        this.dispatch(new actions.SubmitAction(job, {persistable: true, restful: false}));
    }

    remove(job: Job, options?: ActionOptionsArgs): void {
        this.appFacade.objects.file.store.removeByJob(job, options);
        this.appFacade.objects.report.store.removeByJob(job, options);
        this.dispatch(new actions.RemoveAction(job, options));
    }

    attachField(job: Job, field: Field): void {
        if (job.fields.find(f => f.id === field.id)) {
            return;
        }

        job = new Job({
            ...job,
            fields: [...job.fields, field]
        });

        this.dispatch(new actions.SubmitAction(job, {restful: false}));
    }

    detachField(job: Job, field: Field): void {

        if (!job.fields.find(f => f.id === field.id)) {
            return;
        }

        job = new Job({
            ...job,
            fields: job.fields.filter(f => f.id !== field.id)
        });

        this.dispatch(new actions.SubmitAction(job, {restful: false}));
    }

    attachFile(job: Job, file: File): void {

        if (isInArray(file.id, job.files)) {
            return;
        }

        job = new Job({
            ...job,
            files: [...(job.files as Array<string>), file.id]
        });

        this.dispatch(new actions.SubmitAction(job, {restful: false}));
    }

    detachFile(job: Job, file: File): void {

        if (!isInArray(file.id, job.files)) {
            return;
        }

        job = new Job({
            ...job,
            files: [...(job.files as Array<string>).filter(id => id !== file.id)]
        });

        this.dispatch(new actions.SubmitAction(job, {restful: false}));
    }

    attachReport(job: Job, report: Report): void {

        if (isInArray(report.id, job.reports)) {
            return;
        }

        job = new Job({
            ...job,
            reports: [...(job.reports as Array<string>), report.id]
        });

        this.dispatch(new actions.SubmitAction(job, {restful: false}));
    }

    detachReport(job: Job, report: Report): void {

        if (!isInArray(report.id, job.reports)) {
            return;
        }

        job = new Job({
            ...job,
            reports: [...(job.reports as Array<string>).filter(id => id !== report.id)]
        });

        this.dispatch(new actions.SubmitAction(job, {restful: false}));
    }

    clear(): Observable<Array<Job>> {
        return this.select<jobState.State>((state: VasState) => state.job)
            .map(state => state.ids.map(id => state.entities[id]))
            .do(jobs => {
                jobs.forEach(job => this.remove(job, {persistable: true, restful: false}));
            });
    }

    removeByJobType(jobType: JobType, options?: ActionOptionsArgs): void {
        this.removeBy(job => job.jobType === jobType.id, options);
    }

    removeByJobStatus(jobStatus: JobStatus, options?: ActionOptionsArgs): void {
        this.removeBy(job => job.jobStatus === jobStatus.id, options);
    }

    setFilters(filters: jobState.IJobFilters): void {
        this.dispatch(new actions.SetFilterAction(filters));
    }

    setTotal(total: number): void {
        this.dispatch(new actions.SetTotalAction(total));
    }

    loadNextPage(): void {
        return this.dispatch(new actions.LoadNextPage());
    }

    setFilterDefaults(): void {
        this.appFacade.objects.jobStatus.store
            .getAll()
            .map(jobStatuses => jobStatuses.filter(jobStatus => !jobStatus.closed))
            .map(jobStatuses => jobStatuses.map(jobStatus => jobStatus.id))
            .take(1)
            .subscribe(jobStatuses => {
                this.setFilters({
                    jobStatus: jobStatuses,
                    limit: AppConfig.JOB_LIST_PAGE.limit,
                    offset: 0
                });
            });
    }

    setUiLoading(value: boolean): void {
        this.dispatch(new actions.SetUiLoading(value));
    }

    bumpVersion(id: string): void {
        this.dispatch(new actions.BumpVersionAction(id));
    }

    protected submit(job: Job): void {

        let files: Array<File> = [];
        let reports: Array<Report> = [];

        this.appFacade.objects.file.store.getAll().take(1).subscribe(items => files = items);
        this.appFacade.objects.report.store.getAll().take(1).subscribe(items => reports = items);

        // ensure the job does not contain any file/report ids that do not exist (yet)
        job = new Job({
            ...job,
            files: intersect(job.files, files.map(form => form.id)),
            reports: intersect(job.reports, reports.map(form => form.id))
        });

        this.dispatch(new actions.SubmitAction(job));
    }

    wipeJobs(): void {
        this.dispatch(new actions.WipeAction());
    }

    resetOffset(): void {
        this.dispatch(new actions.ResetOffsetAction());
    }

    updateFromApi(jobs: Array<Job>): void {
        this.dispatch(new actions.UpdateFromApi({jobs}));
    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/job/job.store-facade.ts